import { lazy, Suspense, useEffect, useState } from "react";
import { login } from "./api/session";
import { Logo } from "./components/Logo";
import { TabBar, type Tab } from "./components/TabBar";
import { Button, ErrorNote, Skeleton } from "./components/ui";
import { EpochsScreen } from "./screens/Epochs";
import { LeaderboardScreen } from "./screens/Leaderboard";
import { MineScreen } from "./screens/Mine";
import { ReferralsScreen } from "./screens/Referrals";
import { insideTelegram, tg } from "./telegram";

const WalletTab = lazy(() => import("./screens/Wallet"));

type AuthState = { kind: "pending" } | { kind: "ok" } | { kind: "error"; message: string };

export function App() {
  const [tab, setTab] = useState<Tab>("mine");
  const [auth, setAuth] = useState<AuthState>({ kind: "pending" });

  useEffect(() => {
    tg.boot();
    void doLogin();
  }, []);

  // Each tab starts at the top; the back button returns to Mine from any other tab.
  useEffect(() => {
    window.scrollTo({ top: 0 });
    if (tab === "mine") return;
    return tg.backButton.show(() => setTab("mine"));
  }, [tab]);

  async function doLogin() {
    setAuth({ kind: "pending" });
    try {
      await login();
      setAuth({ kind: "ok" });
    } catch (err) {
      const message =
        err instanceof Error && err.message === "NO_INIT_DATA"
          ? "Open this app from Telegram."
          : err instanceof Error
            ? err.message
            : "Sign-in failed.";
      setAuth({ kind: "error", message });
    }
  }

  return (
    <div className="min-h-screen bg-bg-2 text-text">
      <header className="mx-auto flex max-w-md items-center justify-between px-4 pb-2 pt-4">
        <h1 className="flex items-center gap-2 text-lg font-bold tracking-tight">
          <Logo className="h-7 w-7" />
          <span>
            <span className="text-brand">GLOXAR</span> <span className="text-hint">(GXR)</span>
          </span>
        </h1>
        <button
          className="text-xs text-link"
          onClick={() =>
            tg.openLink(import.meta.env.VITE_WEBSITE_URL ?? "https://gloxar.example/official-links")
          }
        >
          Official links
        </button>
      </header>

      <main className="mx-auto max-w-md px-4 pb-24">
        {auth.kind === "pending" && (
          <p className="py-10 text-center text-sm text-hint">Signing in…</p>
        )}
        {auth.kind === "error" && (
          <div className="space-y-3 py-6">
            <ErrorNote>{auth.message}</ErrorNote>
            {!insideTelegram && (
              <p className="text-xs text-hint">
                Running outside Telegram. Set <code>VITE_DEV_INIT_DATA</code> to a signed initData
                string to develop locally.
              </p>
            )}
            <Button variant="secondary" onClick={doLogin}>
              Retry
            </Button>
          </div>
        )}
        {auth.kind === "ok" && (
          <>
            {tab === "mine" && <MineScreen />}
            {tab === "epochs" && <EpochsScreen />}
            {tab === "referrals" && <ReferralsScreen />}
            {tab === "wallet" && (
              <Suspense fallback={<Skeleton className="h-48" />}>
                <WalletTab />
              </Suspense>
            )}
            {tab === "leaderboard" && <LeaderboardScreen />}
          </>
        )}
      </main>

      {auth.kind === "ok" && <TabBar active={tab} onChange={setTab} />}
    </div>
  );
}
